import { useLayoutEffect, useRef } from 'react';

export type RevealVariant = 'up' | 'fade' | 'left' | 'image';

/**
 * Scroll reveal for the public site. Returns a ref; the element animates in (index.css, `[data-rv]`) the first time it
 * enters the viewport. Content is never hidden unless this has run: no IntersectionObserver, reduced motion, or an
 * element already on screen at mount (which simply plays its entrance) all leave it visible.
 */
export function useReveal<T extends HTMLElement = HTMLDivElement>(variant: RevealVariant = 'up', delay = 0) {
  const ref = useRef<T>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    el.setAttribute('data-rv-v', variant);
    if (delay) el.style.setProperty('--rv-delay', `${delay}ms`);
    const play = () => el.setAttribute('data-rv', 'in');
    const r = el.getBoundingClientRect();
    if (r.top < window.innerHeight && r.bottom > 0) { play(); return; }
    el.setAttribute('data-rv', 'wait');
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { play(); io.disconnect(); }
    }, { rootMargin: '0px 0px -8% 0px' });
    io.observe(el);
    return () => { io.disconnect(); if (el.getAttribute('data-rv') === 'wait') el.removeAttribute('data-rv'); };
  }, [variant, delay]);
  return ref;
}
